import { useEffect } from "react";

const SITE_URL = "https://vallartapixel.com";
const DEFAULT_IMAGE = "https://customer-assets.emergentagent.com/job_82ee4e38-abb0-4bd3-985f-83a63905409f/artifacts/3xn4ya4w_Vallarta%20Pixel.png";
const SITE_NAME = "Vallarta Pixel – The PV Hub";

/**
 * Enhanced SEO hook: sets title, description, canonical, OG tags, Twitter cards, robots meta, and JSON-LD structured data.
 *
 * @param {Object} opts
 * @param {string} opts.title - Page title (50-60 chars ideal)
 * @param {string} opts.description - Meta description (120-160 chars ideal)
 * @param {string} [opts.canonical] - Canonical URL path (e.g. "/about")
 * @param {string} [opts.ogType] - OG type (default "website")
 * @param {string} [opts.ogImage] - OG image URL
 * @param {Object} [opts.jsonLd] - JSON-LD structured data object
 * @param {string} [opts.robots] - Robots directive (default "index, follow")
 */
export const useSEO = ({
  title,
  description,
  canonical,
  ogType = "website",
  ogImage,
  jsonLd,
  robots = "index, follow",
}) => {
  useEffect(() => {
    // Title
    if (title) document.title = title;

    // Helpers to manage meta tags
    const setMeta = (attr, key, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    const setLink = (rel, href) => {
      if (!href) return;
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    // Meta description
    setMeta("name", "description", description);

    // Robots
    setMeta("name", "robots", robots);

    // Canonical
    const canonicalUrl = canonical ? `${SITE_URL}${canonical}` : `${SITE_URL}${window.location.pathname}`;
    setLink("canonical", canonicalUrl);

    // Open Graph
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:type", ogType);
    setMeta("property", "og:url", canonicalUrl);
    setMeta("property", "og:image", ogImage || DEFAULT_IMAGE);
    setMeta("property", "og:site_name", SITE_NAME);
    setMeta("property", "og:locale", "en_US");
    setMeta("property", "og:locale:alternate", "es_MX");

    // Twitter Card
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", ogImage || DEFAULT_IMAGE);

    // JSON-LD Structured Data
    let scriptEl = document.getElementById("seo-jsonld");
    if (jsonLd) {
      if (!scriptEl) {
        scriptEl = document.createElement("script");
        scriptEl.id = "seo-jsonld";
        scriptEl.type = "application/ld+json";
        document.head.appendChild(scriptEl);
      }
      scriptEl.textContent = JSON.stringify(jsonLd);
    }

    return () => {
      document.title = SITE_NAME;
      // Clean up JSON-LD
      const existing = document.getElementById("seo-jsonld");
      if (existing) existing.remove();
    };
  }, [title, description, canonical, ogType, ogImage, jsonLd, robots]);
};

export default useSEO;
